import { useMemo, useState } from 'react'
import { Box, CircularProgress, Paper, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { PieChart } from '@mui/x-charts/PieChart'
import { useTranslation } from 'react-i18next'
import { MIN_CARD_HEIGHT } from '../cardHeight'

// Reusable "titled card with a donut chart + custom legend".
// Optionally several series in one card: pass `series` and the items are grouped by that
// key, with a control in the header to switch between them.

const PALETTE = ['#6366f1', '#22c55e', '#f59e0b', '#ec4899', '#06b6d4', '#8b5cf6', '#ef4444', '#14b8a6', '#f97316', '#3b82f6']

export default function DonutBreakdown({
  title,
  items = [],
  isReady = true,
  emptyMessage = 'No data to report.',
  unit = '%',
  size = 240,
  minHeight = MIN_CARD_HEIGHT,
  series: seriesKey,
  countSeries = [],
  defaultSeries,
}) {
  const { t } = useTranslation()

  // one group per distinct series value, in the order the rows arrive
  const groups = useMemo(() => {
    if (!seriesKey) return null
    const map = new Map()
    for (const item of items) {
      const name = item?.[seriesKey]
      if (name == null) continue
      if (!map.has(name)) map.set(name, [])
      map.get(name).push(item)
    }
    return map
  }, [items, seriesKey])

  const names = groups ? [...groups.keys()] : []
  const [picked, setPicked] = useState(null)
  const active =
    picked && groups?.has(picked)
      ? picked
      : defaultSeries && groups?.has(defaultSeries)
        ? defaultSeries
        : names[0]

  const shown = groups ? (groups.get(active) ?? []) : items
  // this series is counted rather than shared out, so the legend carries the count
  const showCount = countSeries.includes(active)

  const series = shown.map((item, index) => ({
    ...item,
    color: item.color || PALETTE[index % PALETTE.length],
  }))

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        minHeight,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} noWrap>
          {title}
        </Typography>
        {isReady && names.length > 1 && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={active}
            onChange={(_, next) => next && setPicked(next)}
            sx={{
              '& .MuiToggleButton-root': {
                px: 0.875,
                py: 0.25,
                border: 'none',
                fontSize: 11,
                lineHeight: 1.6,
                textTransform: 'none',
                color: 'text.secondary',
                '&.Mui-selected': { color: 'primary.main', bgcolor: 'action.selected', fontWeight: 700 },
              },
            }}
          >
            {names.map(name => (
              <ToggleButton key={name} value={name}>
                {t(name)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
      </Stack>

      {!isReady ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 120 }}>
          <CircularProgress size={28} />
        </Box>
      ) : series.length === 0 ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 120 }}>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            {emptyMessage}
          </Typography>
        </Box>
      ) : (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems="center"
          sx={{ minWidth: 0, flex: 1, minHeight: 0 }}
        >
          {/* Donut — fixed square that centers when stacked; clipped (not bleeding) if the card is ever narrower */}
          <Box sx={{ width: size, height: size, maxWidth: '100%', flexShrink: 0, mx: 'auto' }}>
            <PieChart
              series={[
                {
                  data: series,
                  innerRadius: size * 0.28,
                  outerRadius: size * 0.48,
                  paddingAngle: 2,
                  cornerRadius: 5,
                  highlightScope: { fade: 'global', highlight: 'item' },
                  faded: { innerRadius: size * 0.28, additionalRadius: -4, color: 'gray' },
                  valueFormatter: item =>
                    showCount ? `${(item.count ?? item.value).toLocaleString()}` : `${item.value}${unit}`,
                },
              ]}
              width={size}
              height={size}
              margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
              slotProps={{ legend: { hidden: true } }}
            />
          </Box>

          {/* Custom legend — truncates instead of overflowing */}
          {/* only the legend scrolls — the donut stays fixed alongside it */}
          <Stack spacing={1.25} sx={{ flex: 1, width: '100%', minWidth: '0', maxHeight: '100%', overflowY: 'auto', pr: 0.5 }}>
            {series.map((item, index) => (
              <Stack key={item.id ?? item.label ?? index} direction="row" alignItems="center" spacing={1}>
                <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: item.color, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                  {item.label}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary', flexShrink: 0 }}>
                  {showCount ? (item.count ?? item.value).toLocaleString() : `${item.value}${unit}`}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      )}
    </Paper>
  )
}
